import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { CheckCircle2, AlertTriangle, RefreshCw, Trash2, Eye, Search } from "lucide-react";
import { EmailPreview } from "@/components/EmailPreview";

const TEMPLATES = {
  confirmation_eleve: "Confirmation élève",
  nouveau_lead_ecole: "Nouveau lead (auto-école)",
  mise_en_relation_eleve: "Mise en relation (élève)",
  relance_eleve: "Relance élève",
  booking_reserved: "Réservation (élève)",
  booking_admin_new: "Nouveau dossier (admin)",
  booking_school_transfer: "Dossier transmis (auto-école)",
  booking_confirm_request: "Demande de confirmation",
  booking_confirm_reminder: "Rappel de confirmation",
  booking_released: "Place libérée",
  booking_enrolled: "Inscription confirmée",
  booking_neph_doc_reminder: "Pièces manquantes",
  generic: "Autre",
};

export default function AdminEmails() {
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [previewId, setPreviewId] = useState(null);
  const closePreview = useCallback(() => setPreviewId(null), []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (status) params.status = status;
      if (debouncedQ) params.q = debouncedQ;
      const { data } = await api.get("/admin/emails", { params });
      setLogs(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, [status, debouncedQ]);

  useEffect(() => {
    load();
  }, [load]);

  const sent = logs.filter((l) => l.status === "sent" || l.status === "resent").length;

  const deleteOne = async (id) => {
    if (!window.confirm("Supprimer cette entrée du journal ?")) return;
    try {
      await api.delete(`/admin/emails/${id}`);
      toast.success("Entrée supprimée.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteAll = async () => {
    const filtered = status || debouncedQ;
    const label = filtered
      ? `Supprimer les ${logs.length} envoi(s) filtré(s) ?`
      : "Vider TOUT le journal des envois ? Cette action est irréversible.";
    if (!window.confirm(label)) return;
    try {
      const body = debouncedQ ? { ids: logs.map((l) => l.id) } : { all: true, status: status || null };
      const { data } = await api.post("/admin/emails/delete", body);
      toast.success(`${data.deleted} entrée(s) supprimée(s).`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-extrabold tracking-tighter">Journal des envois</h1>
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="resend-all-failed-btn"
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/emails/resend-failed");
                  toast.success(
                    `${data.resent} email(s) renvoyé(s) · ${data.still_failing} échec(s) · ${data.skipped} ignoré(s)`
                  );
                  load();
                } catch (e) {
                  toast.error(apiError(e.response?.data?.detail) || e.message);
                }
              }}
              className="inline-flex items-center gap-2 rounded-md bg-neutral-900 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Renvoyer tous les échecs
            </button>
            <button
              data-testid="delete-all-emails-btn"
              disabled={logs.length === 0}
              onClick={deleteAll}
              className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-white px-4 py-2 text-xs font-bold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" /> {status || debouncedQ ? "Supprimer la liste filtrée" : "Vider le journal"}
            </button>
          </div>
        </div>
        <p className="mt-2 text-neutral-500">
          Chaque email déclenché par la plateforme, avec son statut, sa date et le motif d'échec.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {[
            ["", "Tous"],
            ["sent", "Envoyés"],
            ["failed", "Échecs"],
          ].map(([value, label]) => (
            <button
              key={label}
              onClick={() => setStatus(value)}
              data-testid={`filter-email-${value || "all"}`}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                status === value ? "border-brand bg-brand text-white" : "border-neutral-300 bg-white hover:border-neutral-500"
              }`}
            >
              {label}
            </button>
          ))}
          <label className="relative ml-auto w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher par adresse email ou sujet…"
              data-testid="email-search-input"
              className="w-full rounded-md border border-neutral-300 bg-white py-1.5 pl-8 pr-3 text-xs outline-none focus:border-brand"
            />
          </label>
          <span className="text-xs text-neutral-500" data-testid="email-stats">
            {sent} envoyé(s) sur {logs.length} affiché(s)
          </span>
        </div>

        <div className="mt-4 overflow-x-auto border border-neutral-200 rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="admin-emails-table">
            <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Destinataire</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Auto-école</th>
                <th className="px-4 py-3">Ville</th>
                <th className="px-4 py-3">Canal</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Motif d'échec</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {logs.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-neutral-500" data-testid="admin-no-emails">
                    {debouncedQ ? "Aucun email ne correspond à cette recherche." : "Aucun envoi enregistré."}
                  </td>
                </tr>
              )}
              {logs.map((l) => (
                <tr key={l.id} data-testid={`email-row-${l.id}`} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-neutral-500">
                    {new Date(l.created_at).toLocaleString("fr-FR")}
                  </td>
                  <td className="px-4 py-3 font-semibold">
                    {l.to}
                    <span className="block text-xs font-normal text-neutral-500">{l.subject}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{TEMPLATES[l.template] || l.template}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{l.school_name || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{l.city || "—"}</td>
                  <td className="px-4 py-3 uppercase text-xs text-neutral-500">{l.provider}</td>
                  <td className="px-4 py-3">
                    {l.status === "resent" ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                        <RefreshCw className="h-3 w-3" /> Renvoyé
                      </span>
                    ) : l.status === "sent" ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                        <CheckCircle2 className="h-3 w-3" /> Envoyé
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                        <AlertTriangle className="h-3 w-3" /> Échec
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 max-w-xs truncate text-xs text-neutral-500" title={l.error}>
                    {l.error || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        data-testid={`preview-email-${l.id}`}
                        onClick={() => setPreviewId(l.id)}
                        className="inline-flex items-center gap-1.5 rounded border border-brand/30 bg-brand/5 px-2.5 py-1 text-xs font-bold text-brand transition-colors hover:bg-brand/10"
                      >
                        <Eye className="h-3 w-3" /> Voir
                      </button>
                      <button
                        data-testid={`resend-email-${l.id}`}
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/admin/emails/${l.id}/resend`);
                            if (data.sent) toast.success("Email renvoyé avec succès");
                            else toast.error(`Échec du renvoi : ${data.error}`);
                            load();
                          } catch (e) {
                            toast.error(apiError(e.response?.data?.detail) || e.message);
                          }
                        }}
                        className="inline-flex items-center gap-1.5 rounded border border-neutral-300 px-2.5 py-1 text-xs font-bold transition-colors hover:border-brand hover:text-brand"
                      >
                        <RefreshCw className="h-3 w-3" /> Renvoyer
                      </button>
                      <button
                        data-testid={`delete-email-${l.id}`}
                        onClick={() => deleteOne(l.id)}
                        title="Supprimer cette entrée"
                        className="inline-flex items-center rounded border border-red-200 p-1.5 text-red-600 transition-colors hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <EmailPreview emailId={previewId} onClose={closePreview} />
    </Shell>
  );
}
